import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { paths } from '../../routes/paths';
import { useCreatePlaybookDialog } from '../components/dialogs/create-playbook-dialog';

type ModalView = 'grid' | 'form';

export const usePlaybookManagement = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { actionsV2 } = useFlags();
    const { openCreatePlaybookDialog } = useCreatePlaybookDialog();

    // Modal state - derived from search params when in actionsV2 mode
    const modalState = useMemo(() => {
        if (!actionsV2) return { currentView: 'grid' as ModalView, editingPlaybookId: undefined };

        const editParam = searchParams.get('edit');
        const newParam = searchParams.get('new');

        if (editParam) {
            return { currentView: 'form' as ModalView, editingPlaybookId: editParam };
        } else if (newParam) {
            return { currentView: 'form' as ModalView, editingPlaybookId: undefined };
        } else {
            return { currentView: 'grid' as ModalView, editingPlaybookId: undefined };
        }
    }, [searchParams, actionsV2]);

    // Helper function to update search params while preserving others
    const updateSearchParams = (updates: Record<string, string | null>) => {
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev);
            Object.entries(updates).forEach(([key, value]) => {
                if (value === null) {
                    newParams.delete(key);
                } else {
                    newParams.set(key, value);
                }
            });
            return newParams;
        });
    };

    // Navigation functions
    const openManagePlaybooks = () => {
        if (actionsV2) {
            setIsModalOpen(true);
            // Clear any existing modal params to show grid view
            updateSearchParams({ edit: null, new: null });
        } else {
            navigate(paths.PLAYBOOKS);
        }
    };

    const openCreatePlaybook = () => {
        if (actionsV2) {
            setIsModalOpen(true);
            updateSearchParams({ new: 'true', edit: null });
        } else {
            openCreatePlaybookDialog();
        }
    };

    const editPlaybook = (playbookId: string) => {
        if (actionsV2) {
            setIsModalOpen(true);
            updateSearchParams({ edit: playbookId, new: null });
        } else {
            // Legacy mode - navigate to edit page
            navigate(`${paths.PLAYBOOKS}/${playbookId}/edit`);
        }
    };

    const backToGrid = () => {
        if (actionsV2) {
            updateSearchParams({ edit: null, new: null });
        } else {
            navigate(paths.PLAYBOOKS);
        }
    };

    const closeModal = (open: boolean) => {
        setIsModalOpen(open);
        if (!open && actionsV2) {
            updateSearchParams({ edit: null, new: null });
        }
    };

    // For backward compatibility, expose the old API
    const handleModalOpenChange = (open: boolean) => closeModal(open);

    return {
        // Modal state
        isModalOpen,
        currentView: modalState.currentView,
        editingPlaybookId: modalState.editingPlaybookId,

        // Navigation functions
        openManagePlaybooks,
        openCreatePlaybook,
        editPlaybook,
        backToGrid,
        closeModal: handleModalOpenChange,

        // For modal internal navigation
        navigateToPlaybook: editPlaybook,
        navigateToNew: openCreatePlaybook,
        navigateBack: backToGrid,

        // Flags
        shouldShowModal: actionsV2,
    };
};
